import * as React from 'react';
import { FieldPath, FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { TextField, TextFieldProps } from '../TextField/TextField';

export interface SelectFormItemProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  // -- UseControllerProps -- //
  name: UseControllerProps<TFieldValues, TName>['name'];
  control: UseControllerProps<TFieldValues>['control'];
  /**
   * @default null
   */
  defaultValue?: UseControllerProps<TFieldValues, TName>['defaultValue'];

  // -- TextFieldProps -- //
  variant?: TextFieldProps['variant'];
  disabled?: TextFieldProps['disabled'];
  label: TextFieldProps['label'];
  children: NonNullable<TextFieldProps['children']>;
  fullWidth?: TextFieldProps['fullWidth'];
  onEnter?: TextFieldProps['onEnter'];
  onChange?: TextFieldProps['onChange'];
  required?: TextFieldProps['required'];
  sx?: TextFieldProps['sx'];
  slotProps?: TextFieldProps['slotProps'];
}

/**
 * Convenience wrapper component for simple select form fields.
 */
export const SelectFormItem = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: SelectFormItemProps<TFieldValues, TName>
) => {
  const {
    name,
    control,
    defaultValue,
    disabled,
    variant,
    label,
    children,
    fullWidth,
    onEnter,
    required,
    sx,
    slotProps,
    onChange: onChangeFromProps
  } = props;

  const {
    field: { ref, ...field },
    fieldState: { error }
  } = useController({
    name,
    control,
    defaultValue: defaultValue
  });

  const { onChange, value, ...restOfField } = field;

  return (
    <TextField
      select
      sx={sx}
      disabled={disabled}
      error={!!error}
      helperText={error?.message ?? ''}
      fullWidth={fullWidth !== undefined ? fullWidth : true}
      inputRef={ref}
      label={label}
      required={required}
      variant={variant}
      margin='dense'
      onChange={(e) => {
        onChange(e);
        if (onChangeFromProps) onChangeFromProps(e);
      }}
      onEnter={onEnter}
      slotProps={slotProps}
      // MUI bug with select values of 0 - see https://github.com/mui/material-ui/issues/3157
      value={value === 0 ? `${value}` : value || null}
      {...restOfField}>
      {children}
    </TextField>
  );
};
